import React from 'react';
import { FaTools, FaMapMarkedAlt, FaHeadset, FaCertificate, FaShippingFast, FaCheckCircle } from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      icon: <FaMapMarkedAlt className="text-5xl" />,
      title: 'Free Site Survey',
      description: 'Our experts visit your farm to assess soil type, water source, and crop patterns to design the perfect irrigation system.',
      features: ['Detailed farm analysis', 'Water requirement calculation', 'Custom system design', 'Cost estimation']
    },
    {
      icon: <FaTools className="text-5xl" />,
      title: 'Installation Support',
      description: 'Complete installation guidance and support to ensure your drip irrigation system is set up correctly for optimal performance.',
      features: ['Step-by-step guidance', 'On-site technical support', 'Quality assurance checks', 'Training for maintenance']
    },
    {
      icon: <FaHeadset className="text-5xl" />,
      title: 'After-Sales Service',
      description: 'Dedicated support team available to help you with any queries, maintenance needs, or technical issues.',
      features: ['24/7 phone support', 'Quick problem resolution', 'Spare parts availability', 'Regular maintenance tips']
    },
    {
      icon: <FaCertificate className="text-5xl" />,
      title: 'Quality Assurance',
      description: 'All our products come with quality certification and warranty for your peace of mind.',
      features: ['5-year warranty', 'Quality certifications', 'Performance guarantee', 'Replacement support']
    },
    {
      icon: <FaShippingFast className="text-5xl" />,
      title: 'Fast Delivery',
      description: 'Pan-India delivery network ensures your products reach you quickly and safely.',
      features: ['Quick dispatch', 'Safe packaging', 'Track your order', 'Doorstep delivery']
    },
    {
      icon: <FaCheckCircle className="text-5xl" />,
      title: 'Custom Solutions',
      description: 'Tailored irrigation systems designed specifically for your crop type, farm size, and water availability.',
      features: ['Crop-specific designs', 'Budget-friendly options', 'Scalable systems', 'Expert recommendations']
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#2E368F] text-white py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">Our Services</h1>
          <p className="text-xl text-white max-w-3xl mx-auto">
            Comprehensive support from planning to installation and beyond – we're with you every step of the way
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#F1F8E9]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-[#2E7D32] mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#1B3322]">{service.title}</h3>
                <p className="text-[#4E5D50] mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <FaCheckCircle className="text-[#2E7D32] mt-1 flex-shrink-0" />
                      <span className="text-[#4E5D50] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-[#1B3322]">Our Process</h2>
            <p className="text-lg text-[#4E5D50] max-w-2xl mx-auto">
              Simple, transparent, and efficient – from inquiry to installation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Contact us via phone or WhatsApp. Share your farm details.' },
              { step: '02', title: 'Site Survey', desc: 'Our expert visits your farm for detailed assessment.' },
              { step: '03', title: 'Quotation', desc: 'Receive customized system design with transparent pricing.' },
              { step: '04', title: 'Installation', desc: 'Products delivered and installation support provided.' }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="w-20 h-20 bg-gradient-to-br from-[#2E7D32] to-[#0288D1] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-2xl font-bold text-white display-text">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold mb-2 text-[#1B3322]">{item.title}</h3>
                <p className="text-[#4E5D50]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2E368F] text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-white">
            Contact our experts today for a free consultation and site survey.
          </p>
          <a
            href="https://wa.me/919110278059"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-full px-8 py-4 font-semibold transition-all text-lg"
            data-testid="contact-expert-button"
          >
            Contact Expert on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;